import { useMemo, useState } from 'react';
import {
  useAdminClassrooms,
  useDeleteClassroom,
  usePauseClassroom,
  useSetClassroomAllowPublish,
  useUnpauseClassroom,
} from '../useAdmin';
import type { AdminClassroomRow, ListClassroomsParams } from '../adminApi';
import { AuthApiError } from '../../account/authApi';
import './AdminClassrooms.css';

const PAGE_SIZE = 25;

const PAUSED_OPTIONS: { value: '' | 'true' | 'false'; label: string }[] = [
  { value: '', label: 'Active + paused' },
  { value: 'false', label: 'Active only' },
  { value: 'true', label: 'Paused only' },
];

/**
 * Admin classroom oversight (F6). Search + paused filter +
 * pagination, plus per-row actions (pause/unpause, toggle
 * publishing, delete).
 */
export function AdminClassrooms(): JSX.Element {
  const [q, setQ] = useState('');
  const [paused, setPaused] = useState<'' | 'true' | 'false'>('');
  const [page, setPage] = useState(0);

  const params = useMemo<ListClassroomsParams>(
    () => ({
      q: q.trim() || undefined,
      paused: paused === 'true' ? true : paused === 'false' ? false : undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [q, paused, page],
  );

  const { data, isLoading, isError, error, isFetching } = useAdminClassrooms(params);

  const pauseM = usePauseClassroom();
  const unpauseM = useUnpauseClassroom();
  const allowM = useSetClassroomAllowPublish();
  const deleteM = useDeleteClassroom();
  const lastError = pauseM.error || unpauseM.error || allowM.error || deleteM.error;

  const total = data?.total ?? 0;
  const showing = data?.rows.length ?? 0;
  const startIndex = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const endIndex = page * PAGE_SIZE + showing;
  const canPrev = page > 0;
  const canNext = (page + 1) * PAGE_SIZE < total;

  function reset<T>(setter: (v: T) => void) {
    return (v: T) => {
      setter(v);
      setPage(0);
    };
  }

  return (
    <section className="bl-admin-classrooms" aria-label="Classroom oversight">
      <header className="bl-admin-classrooms__header">
        <div>
          <h2>Classrooms</h2>
          <p className="bl-admin-classrooms__sub">
            Every teacher-managed roster, with an admin-side override for pause and publishing.
          </p>
        </div>
        <p className="bl-admin-classrooms__count" aria-live="polite">
          {isLoading
            ? 'Loading…'
            : total === 0
              ? 'No classrooms match'
              : `${startIndex.toLocaleString()}–${endIndex.toLocaleString()} of ${total.toLocaleString()}`}
          {isFetching && !isLoading && ' · refreshing…'}
        </p>
      </header>

      <form
        className="bl-admin-classrooms__filters"
        onSubmit={(e) => e.preventDefault()}
        role="search"
      >
        <label className="bl-admin-classrooms__field">
          <span className="bl-admin-classrooms__field-label">Name or code contains</span>
          <input
            type="search"
            value={q}
            onChange={(e) => reset(setQ)(e.target.value)}
            placeholder="painters or PAINT001"
            autoComplete="off"
          />
        </label>
        <label className="bl-admin-classrooms__field">
          <span className="bl-admin-classrooms__field-label">Status</span>
          <select
            value={paused}
            onChange={(e) => reset(setPaused)(e.target.value as '' | 'true' | 'false')}
          >
            {PAUSED_OPTIONS.map((o) => (
              <option key={o.value || 'any'} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      </form>

      {(isError || lastError) && (
        <p className="bl-admin-classrooms__error" role="alert">
          {lastError instanceof AuthApiError
            ? lastError.detail || lastError.message
            : (error || lastError) instanceof Error
              ? ((error || lastError) as Error).message
              : 'Could not complete the request.'}
        </p>
      )}

      <div className="bl-admin-classrooms__table-wrap">
        <table className="bl-admin-classrooms__table">
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Code</th>
              <th scope="col">Teacher</th>
              <th scope="col">Status</th>
              <th scope="col">Publishing</th>
              <th scope="col" className="bl-admin-classrooms__num">
                Students
              </th>
              <th scope="col" aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {data?.rows.map((row) => (
              <ClassroomRow
                key={row.id}
                row={row}
                onPause={() => pauseM.mutate({ id: row.id })}
                onUnpause={() => unpauseM.mutate({ id: row.id })}
                onTogglePublishing={() => allowM.mutate({ id: row.id, allow: !row.allow_publish })}
                onDelete={() => {
                  if (
                    window.confirm(
                      `Delete classroom "${row.name}" (${row.class_code})? This cannot be undone.`,
                    )
                  ) {
                    deleteM.mutate({ id: row.id });
                  }
                }}
                busy={
                  pauseM.isPending || unpauseM.isPending || allowM.isPending || deleteM.isPending
                }
              />
            ))}
            {!isLoading && data?.rows.length === 0 && (
              <tr>
                <td colSpan={7} className="bl-admin-classrooms__empty">
                  No classrooms match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <nav className="bl-admin-classrooms__pagination" aria-label="Pagination">
        <button
          type="button"
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          disabled={!canPrev}
        >
          ← Previous
        </button>
        <span className="bl-admin-classrooms__page-label">
          Page {page + 1}
          {total > 0 && ` of ${Math.max(1, Math.ceil(total / PAGE_SIZE))}`}
        </span>
        <button type="button" onClick={() => setPage((p) => p + 1)} disabled={!canNext}>
          Next →
        </button>
      </nav>
    </section>
  );
}

function ClassroomRow({
  row,
  onPause,
  onUnpause,
  onTogglePublishing,
  onDelete,
  busy,
}: {
  row: AdminClassroomRow;
  onPause: () => void;
  onUnpause: () => void;
  onTogglePublishing: () => void;
  onDelete: () => void;
  busy: boolean;
}): JSX.Element {
  const isPaused = !!row.paused_at;
  return (
    <tr data-paused={isPaused || undefined}>
      <td>
        <div className="bl-admin-classrooms__name">{row.name}</div>
        <div className="bl-admin-classrooms__when">since {formatDate(row.created_at)}</div>
      </td>
      <td>
        <code className="bl-admin-classrooms__code">{row.class_code}</code>
      </td>
      <td>{row.teacher_email || '(unknown)'}</td>
      <td>
        {isPaused ? (
          <span className="bl-admin-classrooms__paused">Paused</span>
        ) : (
          <span className="bl-admin-classrooms__active">Active</span>
        )}
      </td>
      <td>
        {row.allow_publish ? (
          <span className="bl-admin-classrooms__pub-on">Allowed</span>
        ) : (
          <span className="bl-admin-classrooms__pub-off">Off</span>
        )}
      </td>
      <td className="bl-admin-classrooms__num">{row.student_count}</td>
      <td>
        <div className="bl-admin-classrooms__row-actions">
          {isPaused ? (
            <button type="button" onClick={onUnpause} disabled={busy}>
              Unpause
            </button>
          ) : (
            <button type="button" onClick={onPause} disabled={busy}>
              Pause
            </button>
          )}
          <button type="button" onClick={onTogglePublishing} disabled={busy}>
            {row.allow_publish ? 'Disable publish' : 'Allow publish'}
          </button>
          <button
            type="button"
            onClick={onDelete}
            disabled={busy}
            className="bl-admin-classrooms__row-action--danger"
          >
            Delete
          </button>
        </div>
      </td>
    </tr>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}
